import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../providers/ThemeProvider';
import {
    Settings,
    User,
    Bell,
    CreditCard,
    Shield,
    Palette,
    Globe,
    Mail,
    Phone,
    Camera,
    Save,
    Loader2,
    Check,
    Moon,
    Sun,
    LogOut,
    Trash2,
    Key,
    Building,
    ArrowRight,
    Monitor,
} from 'lucide-react';
import { Link } from 'react-router-dom';

interface ProfileSettings {
    full_name: string;
    email: string;
    phone: string;
    avatar_url: string;
}

interface TenantSettings {
    name: string;
    slug: string;
    logo_url: string;
    primary_color: string;
}

interface NotificationSettings {
    email_new_student: boolean;
    email_payment_received: boolean;
    email_weekly_summary: boolean;
    push_new_message: boolean;
    push_workout_completed: boolean;
}

type TabType = 'profile' | 'business' | 'notifications' | 'billing' | 'appearance' | 'security';

export function SettingsPage() {
    const { profile, tenant, signOut } = useAuth();
    const queryClient = useQueryClient();
    const [activeTab, setActiveTab] = useState<TabType>('profile');
    const [saved, setSaved] = useState(false);

    // Profile form
    const [profileForm, setProfileForm] = useState<ProfileSettings>({
        full_name: '',
        email: '',
        phone: '',
        avatar_url: '',
    });

    // Tenant form
    const [tenantForm, setTenantForm] = useState<TenantSettings>({
        name: '',
        slug: '',
        logo_url: '',
        primary_color: '#7C3AED',
    });

    // Notifications form
    const [notificationsForm, setNotificationsForm] = useState<NotificationSettings>({
        email_new_student: true,
        email_payment_received: true,
        email_weekly_summary: true,
        push_new_message: true,
        push_workout_completed: true,
    });

    // Theme from provider
    const { theme, setTheme } = useTheme();

    // Load profile data
    useEffect(() => {
        if (profile) {
            const p = profile as any;
            setProfileForm({
                full_name: p.full_name || '',
                email: p.email || '',
                phone: p.phone || '',
                avatar_url: p.avatar_url || '',
            });
        }
    }, [profile]);

    // Load tenant data
    useEffect(() => {
        if (tenant) {
            const tenantData = tenant as unknown as Record<string, unknown>;
            setTenantForm({
                name: tenant.name || '',
                slug: tenant.slug || '',
                logo_url: tenant.logo_url || '',
                primary_color: (tenantData.primary_color as string) || '#7C3AED',
            });
        }
    }, [tenant]);

    // Save profile
    const saveProfileMutation = useMutation({
        mutationFn: async (data: ProfileSettings) => {
            if (!profile?.id) return;

            const { error } = await supabase
                .from('users')
                .update({
                    full_name: data.full_name,
                    phone: data.phone,
                    avatar_url: data.avatar_url || null,
                })
                .eq('id', profile.id);

            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['profile'] });
            showSaved();
        },
    });

    // Save tenant
    const saveTenantMutation = useMutation({
        mutationFn: async (data: TenantSettings) => {
            if (!tenant?.id) return;

            const { error } = await supabase
                .from('tenants')
                .update({
                    name: data.name,
                    logo_url: data.logo_url || null,
                    primary_color: data.primary_color,
                })
                .eq('id', tenant.id);

            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['tenant'] });
            showSaved();
        },
    });

    const showSaved = () => {
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
    };

    const tabs = [
        { id: 'profile' as TabType, label: 'Perfil', icon: User },
        { id: 'business' as TabType, label: 'Negócio', icon: Building },
        { id: 'billing' as TabType, label: 'Assinatura', icon: CreditCard },
        { id: 'notifications' as TabType, label: 'Notificações', icon: Bell },
        { id: 'appearance' as TabType, label: 'Aparência', icon: Palette },
        { id: 'security' as TabType, label: 'Segurança', icon: Shield },
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="section-title flex items-center gap-2">
                    <Settings className="w-6 h-6" />
                    Configurações
                </h1>
                <p className="section-subtitle">
                    Gerencie seu perfil e preferências
                </p>
            </div>

            {/* Saved indicator */}
            {saved && (
                <div className="fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 z-50 animate-in slide-in-from-top">
                    <Check className="w-4 h-4" />
                    Salvo com sucesso!
                </div>
            )}

            <div className="flex flex-col lg:flex-row gap-6">
                {/* Sidebar */}
                <div className="lg:w-64 flex-shrink-0">
                    <div className="card p-2">
                        <nav className="space-y-1">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${activeTab === tab.id
                                        ? 'bg-primary-50 text-primary-700 font-medium'
                                        : 'text-gray-600 hover:bg-gray-50'
                                        }`}
                                >
                                    <tab.icon className="w-5 h-5" />
                                    {tab.label}
                                </button>
                            ))}
                        </nav>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1">
                    {/* Profile Tab */}
                    {activeTab === 'profile' && (
                        <div className="card">
                            <h2 className="font-semibold mb-6 flex items-center gap-2">
                                <User className="w-5 h-5" />
                                Informações do Perfil
                            </h2>

                            <form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    saveProfileMutation.mutate(profileForm);
                                }}
                                className="space-y-6"
                            >
                                {/* Avatar */}
                                <div className="flex items-center gap-6">
                                    <div className="relative">
                                        <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white text-3xl font-bold overflow-hidden">
                                            {profileForm.avatar_url ? (
                                                <img
                                                    src={profileForm.avatar_url}
                                                    alt=""
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                profileForm.full_name?.charAt(0).toUpperCase() || 'U'
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            className="absolute bottom-0 right-0 p-2 bg-white rounded-full shadow-lg border hover:bg-gray-50"
                                        >
                                            <Camera className="w-4 h-4" />
                                        </button>
                                    </div>
                                    <div>
                                        <h3 className="font-medium text-gray-900">{profileForm.full_name}</h3>
                                        <p className="text-sm text-gray-500">{profile?.role === 'personal' ? 'Personal Trainer' : profile?.role}</p>
                                    </div>
                                </div>

                                <div className="grid gap-4 md:grid-cols-2">
                                    <div>
                                        <label className="label">Nome Completo</label>
                                        <input
                                            type="text"
                                            value={profileForm.full_name}
                                            onChange={(e) =>
                                                setProfileForm({ ...profileForm, full_name: e.target.value })
                                            }
                                            className="input"
                                        />
                                    </div>
                                    <div>
                                        <label className="label">Email</label>
                                        <input
                                            type="email"
                                            value={profileForm.email}
                                            disabled
                                            className="input bg-gray-50"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">Email não pode ser alterado</p>
                                    </div>
                                    <div>
                                        <label className="label">Telefone</label>
                                        <input
                                            type="tel"
                                            value={profileForm.phone}
                                            onChange={(e) =>
                                                setProfileForm({ ...profileForm, phone: e.target.value })
                                            }
                                            placeholder="(11) 99999-9999"
                                            className="input"
                                        />
                                    </div>
                                    <div>
                                        <label className="label">URL do Avatar</label>
                                        <input
                                            type="url"
                                            value={profileForm.avatar_url}
                                            onChange={(e) =>
                                                setProfileForm({ ...profileForm, avatar_url: e.target.value })
                                            }
                                            placeholder="https://..."
                                            className="input"
                                        />
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={saveProfileMutation.isPending}
                                    className="btn-primary"
                                >
                                    {saveProfileMutation.isPending ? (
                                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                    ) : (
                                        <Save className="w-4 h-4 mr-2" />
                                    )}
                                    Salvar Alterações
                                </button>
                            </form>
                        </div>
                    )}

                    {/* Business Tab */}
                    {activeTab === 'business' && (
                        <div className="card">
                            <h2 className="font-semibold mb-6 flex items-center gap-2">
                                <Building className="w-5 h-5" />
                                Configurações do Negócio
                            </h2>

                            <form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    saveTenantMutation.mutate(tenantForm);
                                }}
                                className="space-y-6"
                            >
                                <div className="grid gap-4 md:grid-cols-2">
                                    <div>
                                        <label className="label">Nome do Negócio</label>
                                        <input
                                            type="text"
                                            value={tenantForm.name}
                                            onChange={(e) =>
                                                setTenantForm({ ...tenantForm, name: e.target.value })
                                            }
                                            className="input"
                                        />
                                    </div>
                                    <div>
                                        <label className="label">Slug (URL)</label>
                                        <div className="flex items-center">
                                            <span className="px-3 py-2 bg-gray-100 border border-r-0 rounded-l-lg text-gray-500 text-sm">
                                                fitflow.app/
                                            </span>
                                            <input
                                                type="text"
                                                value={tenantForm.slug}
                                                disabled
                                                className="input rounded-l-none bg-gray-50"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="label">Logo URL</label>
                                        <input
                                            type="url"
                                            value={tenantForm.logo_url}
                                            onChange={(e) =>
                                                setTenantForm({ ...tenantForm, logo_url: e.target.value })
                                            }
                                            placeholder="https://..."
                                            className="input"
                                        />
                                    </div>
                                    <div>
                                        <label className="label">Cor Principal</label>
                                        <div className="flex gap-2">
                                            <input
                                                type="color"
                                                value={tenantForm.primary_color}
                                                onChange={(e) =>
                                                    setTenantForm({ ...tenantForm, primary_color: e.target.value })
                                                }
                                                className="w-12 h-10 rounded-lg border cursor-pointer"
                                            />
                                            <input
                                                type="text"
                                                value={tenantForm.primary_color}
                                                onChange={(e) =>
                                                    setTenantForm({ ...tenantForm, primary_color: e.target.value })
                                                }
                                                className="input flex-1"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={saveTenantMutation.isPending}
                                    className="btn-primary"
                                >
                                    {saveTenantMutation.isPending ? (
                                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                    ) : (
                                        <Save className="w-4 h-4 mr-2" />
                                    )}
                                    Salvar Alterações
                                </button>
                            </form>
                        </div>
                    )}

                    {/* Billing Tab (Redirect) */}
                    {activeTab === 'billing' && (
                        <div className="card text-center py-12">
                            <CreditCard className="w-16 h-16 text-primary-200 mx-auto mb-4" />
                            <h2 className="text-xl font-bold text-gray-900 mb-2">Gerenciamento de Assinatura</h2>
                            <p className="text-gray-500 max-w-md mx-auto mb-8">
                                Veja seu plano atual, histórico de faturas e métodos de pagamento em nossa área dedicada de faturamento.
                            </p>
                            <Link to="/billing" className="btn-primary">
                                Ir para Faturamento
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </Link>
                        </div>
                    )}


                    {/* Notifications Tab */}
                    {activeTab === 'notifications' && (
                        <div className="card">
                            <h2 className="font-semibold mb-6 flex items-center gap-2">
                                <Bell className="w-5 h-5" />
                                Preferências de Notificação
                            </h2>

                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-sm font-medium text-gray-900 mb-4 flex items-center gap-2">
                                        <Mail className="w-4 h-4" />
                                        Notificações por Email
                                    </h3>
                                    <div className="space-y-3">
                                        <NotificationToggle
                                            label="Novo aluno cadastrado"
                                            description="Receba um email quando um novo aluno aceitar seu convite"
                                            checked={notificationsForm.email_new_student}
                                            onChange={(v) =>
                                                setNotificationsForm({ ...notificationsForm, email_new_student: v })
                                            }
                                        />
                                        <NotificationToggle
                                            label="Pagamento recebido"
                                            description="Notificação quando uma cobrança for paga"
                                            checked={notificationsForm.email_payment_received}
                                            onChange={(v) =>
                                                setNotificationsForm({ ...notificationsForm, email_payment_received: v })
                                            }
                                        />
                                        <NotificationToggle
                                            label="Resumo semanal"
                                            description="Receba um resumo toda segunda-feira"
                                            checked={notificationsForm.email_weekly_summary}
                                            onChange={(v) =>
                                                setNotificationsForm({ ...notificationsForm, email_weekly_summary: v })
                                            }
                                        />
                                    </div>
                                </div>

                                <hr />

                                <div>
                                    <h3 className="text-sm font-medium text-gray-900 mb-4 flex items-center gap-2">
                                        <Bell className="w-4 h-4" />
                                        Notificações Push
                                    </h3>
                                    <div className="space-y-3">
                                        <NotificationToggle
                                            label="Nova mensagem"
                                            description="Receba notificação quando um aluno enviar mensagem"
                                            checked={notificationsForm.push_new_message}
                                            onChange={(v) =>
                                                setNotificationsForm({ ...notificationsForm, push_new_message: v })
                                            }
                                        />
                                        <NotificationToggle
                                            label="Treino concluído"
                                            description="Quando um aluno completar um treino"
                                            checked={notificationsForm.push_workout_completed}
                                            onChange={(v) =>
                                                setNotificationsForm({ ...notificationsForm, push_workout_completed: v })
                                            }
                                        />
                                    </div>
                                </div>

                                <button onClick={showSaved} className="btn-primary">
                                    <Save className="w-4 h-4 mr-2" />
                                    Salvar Preferências
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Appearance Tab */}
                    {activeTab === 'appearance' && (
                        <div className="card">
                            <h2 className="font-semibold mb-6 flex items-center gap-2">
                                <Palette className="w-5 h-5" />
                                Aparência
                            </h2>

                            <div className="space-y-6">
                                <div>
                                    <label className="label">Tema</label>
                                    <div className="grid grid-cols-3 gap-4">
                                        {[
                                            { value: 'light', label: 'Claro', icon: Sun },
                                            { value: 'dark', label: 'Escuro', icon: Moon },
                                            { value: 'system', label: 'Sistema', icon: Monitor },
                                        ].map((t) => (
                                            <button
                                                key={t.value}
                                                onClick={() => setTheme(t.value as 'light' | 'dark' | 'system')}
                                                className={`flex flex-col items-center gap-2 p-4 rounded-xl border transition-colors ${theme === t.value
                                                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30'
                                                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                                                    }`}
                                            >
                                                <t.icon className={`w-6 h-6 ${theme === t.value ? 'text-purple-600 dark:text-purple-400' : 'text-gray-400'}`} />
                                                <span className={`font-medium ${theme === t.value ? 'text-purple-700 dark:text-purple-300' : 'text-gray-700 dark:text-gray-300'}`}>
                                                    {t.label}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                                    O tema é salvo automaticamente no seu perfil! 💜
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Security Tab */}
                    {activeTab === 'security' && (
                        <div className="space-y-6">
                            <div className="card">
                                <h2 className="font-semibold mb-6 flex items-center gap-2">
                                    <Key className="w-5 h-5" />
                                    Alterar Senha
                                </h2>

                                <form className="space-y-4 max-w-md">
                                    <div>
                                        <label className="label">Senha Atual</label>
                                        <input type="password" className="input" placeholder="••••••••" />
                                    </div>
                                    <div>
                                        <label className="label">Nova Senha</label>
                                        <input type="password" className="input" placeholder="••••••••" />
                                    </div>
                                    <div>
                                        <label className="label">Confirmar Nova Senha</label>
                                        <input type="password" className="input" placeholder="••••••••" />
                                    </div>
                                    <button type="button" className="btn-primary">
                                        <Key className="w-4 h-4 mr-2" />
                                        Alterar Senha
                                    </button>
                                </form>
                            </div>

                            <div className="card border-red-200 bg-red-50">
                                <h2 className="font-semibold mb-4 text-red-900 flex items-center gap-2">
                                    <Trash2 className="w-5 h-5" />
                                    Zona de Perigo
                                </h2>

                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="font-medium text-red-900">Sair da Conta</p>
                                            <p className="text-sm text-red-700">Encerrar sua sessão atual</p>
                                        </div>
                                        <button onClick={signOut} className="btn-secondary text-red-600 border-red-300 hover:bg-red-100">
                                            <LogOut className="w-4 h-4 mr-2" />
                                            Sair
                                        </button>
                                    </div>
                                    <hr className="border-red-200" />
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="font-medium text-red-900">Excluir Conta</p>
                                            <p className="text-sm text-red-700">Esta ação é irreversível</p>
                                        </div>
                                        <button className="btn text-red-600 border-red-300 hover:bg-red-100">
                                            <Trash2 className="w-4 h-4 mr-2" />
                                            Excluir
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// Notification Toggle Component
function NotificationToggle({
    label,
    description,
    checked,
    onChange,
}: {
    label: string;
    description: string;
    checked: boolean;
    onChange: (value: boolean) => void;
}) {
    return (
        <label className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 cursor-pointer">
            <div>
                <p className="font-medium text-gray-900">{label}</p>
                <p className="text-sm text-gray-500">{description}</p>
            </div>
            <div
                onClick={(e) => {
                    e.preventDefault();
                    onChange(!checked);
                }}
                className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${checked ? 'bg-primary-600' : 'bg-gray-300'
                    }`}
            >
                <div
                    className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'
                        }`}
                />
            </div>
        </label>
    );
}
